/**
 * Admin Dashboard - Overview Page
 * Beautiful dashboard with real-time statistics, charts, and activity feed
 */

import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';
import { useNavigate } from 'react-router-dom';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalTables: number;
  activeTables: number;
  totalGames: number;
  todayGames: number;
  totalBots: number;
  totalRevenue: number;
  userGrowth?: number; // Percentage growth this week
  revenueGrowth?: number;
  userGrowthData?: { date: string; count: number }[];
  gamesPerDay?: { date: string; count: number }[];
  roleDistribution?: { role: string; count: number }[];
}

interface ActivityItem {
  id: string;
  type: 'user' | 'game' | 'win' | 'bot';
  message: string;
  timestamp: Date;
  icon: string;
  color: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    loadDashboardStats();
    loadRecentActivity();

    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      loadDashboardStats();
      loadRecentActivity();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const data = await adminService.getDashboardOverview();
      setStats(data);
      setError(null);
      setLastUpdate(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard stats');
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentActivity = async () => {
    try {
      const activityData = await adminService.getActivityMetrics();
      // Transform activity data into feed items
      const activities: ActivityItem[] = [];

      // Add recent users (simulated - replace with real data)
      if (activityData.recentUsers) {
        activityData.recentUsers.forEach((user: any) => {
          activities.push({
            id: `user-${user.id}`,
            type: 'user',
            message: `New user registered: ${user.username}`,
            timestamp: new Date(user.createdAt),
            icon: '👤',
            color: 'text-blue-600',
          });
        });
      }

      // Add recent games (simulated - replace with real data)
      if (activityData.recentGames) {
        activityData.recentGames.forEach((game: any) => {
          activities.push({
            id: `game-${game.id}`,
            type: 'game',
            message: `Game started at ${game.tableName}`,
            timestamp: new Date(game.startTime),
            icon: '🎲',
            color: 'text-green-600',
          });
        });
      }

      // Sort by timestamp descending
      activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setActivities(activities.slice(0, 10));
    } catch (err) {
      console.error('Failed to load activity:', err);
      // Fallback to simulated data
      generateSimulatedActivity();
    }
  };

  const generateSimulatedActivity = () => {
    const now = new Date();
    const simulatedActivities: ActivityItem[] = [
      {
        id: '1',
        type: 'user',
        message: 'New user registered: PokerPro123',
        timestamp: new Date(now.getTime() - 2 * 60000),
        icon: '👤',
        color: 'text-blue-600',
      },
      {
        id: '2',
        type: 'game',
        message: 'Game started at High Stakes Table',
        timestamp: new Date(now.getTime() - 5 * 60000),
        icon: '🎲',
        color: 'text-green-600',
      },
      {
        id: '3',
        type: 'win',
        message: 'Big win: Player123 won 50,000 chips!',
        timestamp: new Date(now.getTime() - 8 * 60000),
        icon: '🏆',
        color: 'text-yellow-600',
      },
      {
        id: '4',
        type: 'bot',
        message: 'Bot "RoboPlayer" deployed to Table 3',
        timestamp: new Date(now.getTime() - 12 * 60000),
        icon: '🤖',
        color: 'text-purple-600',
      },
      {
        id: '5',
        type: 'user',
        message: 'New user registered: CardShark99',
        timestamp: new Date(now.getTime() - 15 * 60000),
        icon: '👤',
        color: 'text-blue-600',
      },
    ];
    setActivities(simulatedActivities);
  };

  const formatTimeAgo = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  // Loading skeleton
  if (loading && !stats) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6 h-32">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6 h-80">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-64 bg-gray-100 rounded"></div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 h-80">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-64 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-600 mb-4">Error: {error}</p>
        <button
          onClick={loadDashboardStats}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.totalUsers || 0,
      subtitle: `${stats?.activeUsers || 0} active now`,
      growth: stats?.userGrowth || 12.5,
      icon: '👥',
      color: 'bg-blue-500',
      gradientFrom: 'from-blue-500',
      gradientTo: 'to-blue-600',
    },
    {
      title: 'Active Tables',
      value: stats?.activeTables || 0,
      subtitle: `${stats?.totalTables || 0} total tables`,
      growth: 8.3,
      icon: '🎲',
      color: 'bg-green-500',
      gradientFrom: 'from-green-500',
      gradientTo: 'to-green-600',
    },
    {
      title: 'Games Played',
      value: stats?.totalGames || 0,
      subtitle: `${stats?.todayGames || 0} today`,
      growth: 15.2,
      icon: '🃏',
      color: 'bg-purple-500',
      gradientFrom: 'from-purple-500',
      gradientTo: 'to-purple-600',
    },
    {
      title: 'Total Revenue',
      value: `${((stats?.totalRevenue || 0) / 1000).toFixed(1)}K`,
      subtitle: 'Chips in circulation',
      growth: stats?.revenueGrowth || 20.1,
      icon: '💰',
      color: 'bg-orange-500',
      gradientFrom: 'from-orange-500',
      gradientTo: 'to-orange-600',
    },
  ];

  // Simple bar chart data (games per day - last 7 days)
  const gamesData = stats?.gamesPerDay || [
    { date: 'Mon', count: 45 },
    { date: 'Tue', count: 52 },
    { date: 'Wed', count: 38 },
    { date: 'Thu', count: 65 },
    { date: 'Fri', count: 72 },
    { date: 'Sat', count: 88 },
    { date: 'Sun', count: 95 },
  ];
  const maxGames = Math.max(...gamesData.map((d) => d.count));

  // Role distribution data
  const roleData = stats?.roleDistribution || [
    { role: 'USER', count: 850, color: 'bg-gray-500' },
    { role: 'ADMIN', count: 12, color: 'bg-blue-500' },
    { role: 'SUPERADMIN', count: 3, color: 'bg-purple-500' },
  ];
  const totalRoles = roleData.reduce((sum, r) => sum + r.count, 0);

  return (
    <div className="space-y-6">
      {/* Header with Last Updated */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Dashboard Overview</h2>
            <p className="text-blue-100 mt-1">Real-time platform analytics</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-blue-100">Last updated</div>
            <div className="text-sm font-medium">{lastUpdate.toLocaleTimeString()}</div>
            <div className="mt-2 flex items-center justify-end space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-xs text-green-200">Live</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
          >
            <div className={`bg-gradient-to-r ${card.gradientFrom} ${card.gradientTo} p-4`}>
              <div className="flex items-center justify-between">
                <div className="text-4xl">{card.icon}</div>
                <div className="text-white text-right">
                  <div
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                      card.growth >= 0
                        ? 'bg-green-400 bg-opacity-30 text-green-100'
                        : 'bg-red-400 bg-opacity-30 text-red-100'
                    }`}
                  >
                    {card.growth >= 0 ? '↑' : '↓'} {Math.abs(card.growth)}%
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-500 text-sm font-medium">{card.title}</p>
              <h3 className="text-3xl font-bold text-gray-800 mt-2 group-hover:text-blue-600 transition-colors">
                {typeof card.value === 'number'
                  ? card.value.toLocaleString()
                  : card.value}
              </h3>
              <p className="text-gray-400 text-xs mt-1">{card.subtitle}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Games Per Day Bar Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-bold text-gray-800">Games Per Day</h4>
            <span className="text-xs text-gray-500">Last 7 days</span>
          </div>
          <div className="h-64">
            <div className="flex items-end justify-between h-full space-x-2">
              {gamesData.map((day, index) => (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div className="w-full flex flex-col items-center justify-end h-full pb-2">
                    <div
                      className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg transition-all duration-500 hover:from-blue-600 hover:to-blue-500 relative group cursor-pointer"
                      style={{ height: `${(day.count / maxGames) * 100}%` }}
                    >
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {day.count} games
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 mt-2 font-medium">
                    {day.date}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Role Distribution Pie Chart */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h4 className="text-lg font-bold text-gray-800 mb-6">User Roles</h4>
          <div className="flex flex-col items-center">
            {/* Simple Pie Chart */}
            <div className="relative w-48 h-48 mb-6">
              <svg viewBox="0 0 100 100" className="transform -rotate-90">
                {roleData.map((role, index) => {
                  const previousTotal = roleData
                    .slice(0, index)
                    .reduce((sum, r) => sum + r.count, 0);
                  const percentage = (role.count / totalRoles) * 100;
                  const offset = (previousTotal / totalRoles) * 100;

                  return (
                    <circle
                      key={index}
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                      stroke={`currentColor`}
                      strokeWidth="20"
                      strokeDasharray={`${percentage} ${100 - percentage}`}
                      strokeDashoffset={-offset}
                      className={
                        role.role === 'SUPERADMIN'
                          ? 'text-purple-500'
                          : role.role === 'ADMIN'
                          ? 'text-blue-500'
                          : 'text-gray-400'
                      }
                    />
                  );
                })}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-800">
                    {totalRoles}
                  </div>
                  <div className="text-xs text-gray-500">Total</div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="w-full space-y-2">
              {roleData.map((role, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        role.role === 'SUPERADMIN'
                          ? 'bg-purple-500'
                          : role.role === 'ADMIN'
                          ? 'bg-blue-500'
                          : 'bg-gray-400'
                      }`}
                    ></div>
                    <span className="text-sm text-gray-600">{role.role}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-800">
                    {role.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section: Quick Actions + Activity Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-lg p-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4">Quick Actions</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/admin/tables')}
              className="group p-6 bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-500 hover:to-blue-600 rounded-lg transition-all duration-300 text-left border-2 border-blue-200 hover:border-blue-500"
            >
              <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">
                🎲
              </div>
              <div className="font-bold text-blue-800 group-hover:text-white transition-colors">
                Create Table
              </div>
              <div className="text-sm text-blue-600 group-hover:text-blue-100 transition-colors mt-1">
                Start a new game
              </div>
            </button>

            <button
              onClick={() => navigate('/admin/bots')}
              className="group p-6 bg-gradient-to-br from-purple-50 to-purple-100 hover:from-purple-500 hover:to-purple-600 rounded-lg transition-all duration-300 text-left border-2 border-purple-200 hover:border-purple-500"
            >
              <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">
                🤖
              </div>
              <div className="font-bold text-purple-800 group-hover:text-white transition-colors">
                Deploy Bot
              </div>
              <div className="text-sm text-purple-600 group-hover:text-purple-100 transition-colors mt-1">
                Add AI player
              </div>
            </button>

            <button
              onClick={() => alert('Broadcast notification feature coming soon!')}
              className="group p-6 bg-gradient-to-br from-orange-50 to-orange-100 hover:from-orange-500 hover:to-orange-600 rounded-lg transition-all duration-300 text-left border-2 border-orange-200 hover:border-orange-500"
            >
              <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">
                📢
              </div>
              <div className="font-bold text-orange-800 group-hover:text-white transition-colors">
                Send Notification
              </div>
              <div className="text-sm text-orange-600 group-hover:text-orange-100 transition-colors mt-1">
                Broadcast message
              </div>
            </button>
          </div>
        </div>

        {/* Live Activity Feed */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-800">Live Activity</h4>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-gray-500">Auto-refresh</span>
            </div>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {activities.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <div className="text-4xl mb-2">📊</div>
                <div className="text-sm">No recent activity</div>
              </div>
            ) : (
              activities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className={`text-2xl ${activity.color}`}>
                    {activity.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 leading-tight">
                      {activity.message}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatTimeAgo(activity.timestamp)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
